import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Role, Scope, Resource, ROLE_HIERARCHY } from '@/types/rbac';
import type { MemberScopes } from '@/types/rbac';

export interface PermissionsApi {
  role: Role | null;
  isAdmin: boolean;
  isGerente: boolean;
  isUsuario: boolean;
  hasRole: (requiredRole: Role) => boolean;
  hasScope: (resource: Resource, scope: Scope) => boolean;
  hasAnyScope: (resource: Resource, scopes: Scope[]) => boolean;
  hasAllScopes: (resource: Resource, scopes: Scope[]) => boolean;
}

export const usePermissions = (): PermissionsApi => {
  const role = useAuthStore((state) => state.role);
  const scopes = useAuthStore((state) => state.scopes);
  const globalAccess = useAuthStore((state) => state.globalAccess);

  return useMemo(() => {
    const isAdmin = role === Role.ADMIN || globalAccess;
    const isGerente = role === Role.GERENTE;
    const isUsuario = role === Role.USUARIO;

    const hasRole = (requiredRole: Role): boolean => {
      if (!role) return false;
      if (isAdmin) return true;
      return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole];
    };

    const hasScope = (resource: Resource, scope: Scope): boolean => {
      if (!role) return false;
      if (isAdmin) return true;
      const resourceScopes = scopes[resource];
      return resourceScopes?.includes(scope) ?? false;
    };

    const hasAnyScope = (resource: Resource, scopeList: Scope[]): boolean => {
      if (!role) return false;
      if (isAdmin) return true;
      return scopeList.some((s) => hasScope(resource, s));
    };

    const hasAllScopes = (resource: Resource, scopeList: Scope[]): boolean => {
      if (!role) return false;
      if (isAdmin) return true;
      return scopeList.every((s) => hasScope(resource, s));
    };

    return {
      role,
      isAdmin,
      isGerente,
      isUsuario,
      hasRole,
      hasScope,
      hasAnyScope,
      hasAllScopes,
    };
  }, [role, scopes, globalAccess]);
};
